import {
  IBookingBase,
  IBookingCheckinBase,
  IBookingPaymentBase,
} from './bookings';
import { IEntityBase } from './entityBase';
import { IFeatureGroupBase } from './features';
import { IUserBase } from './users';

export enum WaveSideEnum {
  COMBO = 'Combo',
  LEFT = 'Esquerda',
  RIGHT = 'Direita',
}

export interface IActivityScheduleBlockBase extends IEntityBase {
  blockedDate: string;
  activityScheduleId: string;
}

export type ActivityScheduleSessionBlockReason =
  | 'WEATHER_CONDITIONS'
  | 'POWER_OUTAGE'
  | 'TECHNICAL_ISSUES'
  | 'OTHER';

export type ActivityScheduleSessionBlockStatus = 'ACTIVE' | 'UNBLOCKED';

export type ActivityScheduleSessionBlockBookingStatus =
  | 'SUSPENDED'
  | 'PENDING_CONFIRMATION'
  | 'CONFIRMED_BY_USER'
  | 'DECLINED_BY_USER'
  | 'MANUAL_HANDLING';

export interface IBlockableActivitySchedule {
  activityScheduleId: string;
  booked: number;
  date: string;
  endTime: string;
  isBlocked: boolean;
  modalityTitle?: string;
  queueBooked: number;
  startTime: string;
  waveSide?: 'COMBO' | 'LEFT' | 'RIGHT';
}

export interface IActivityScheduleSessionBlockItemBase extends IEntityBase {
  activitySchedule?: IActivityScheduleBase;
  activityScheduleId: string;
  blockId: string;
  endTimeSnapshot: string;
  startTimeSnapshot: string;
}

export interface IActivityScheduleSessionBlockBookingBase extends IEntityBase {
  blockId: string;
  bookingId: string;
  userId: string;
  status: ActivityScheduleSessionBlockBookingStatus;
}

export interface IActivityScheduleSessionBlockQueueBase extends IEntityBase {
  blockId: string;
  bookingQueueId: string;
  userId: string;
  status: 'SUSPENDED' | 'RELEASED' | 'MANUAL_HANDLING';
}

export interface IActivityScheduleSessionBlockBase extends IEntityBase {
  activityId: string;
  blockedDate: string;
  bookings: IActivityScheduleSessionBlockBookingBase[];
  customReason?: string | null;
  items: IActivityScheduleSessionBlockItemBase[];
  notificationMessage: string;
  queues: IActivityScheduleSessionBlockQueueBase[];
  reason: ActivityScheduleSessionBlockReason;
  status: ActivityScheduleSessionBlockStatus;
  unblockedAt?: string | null;
  ventureId: string;
}

export interface IWeekdayActivityScheduleCheckin extends IBookingCheckinBase {
  user: IUserBase;
}

interface IEventScheduleBookingUser extends IUserBase {
  host?: IUserBase;
  featureGroup: IFeatureGroupBase;
}

export interface IEventsScheduleBooking extends IBookingBase {
  checkins: IWeekdayActivityScheduleCheckin[];
  payment?: IBookingPaymentBase;
  user: IEventScheduleBookingUser;
}

export interface IEventsBookingQueues extends IBookingBase {
  id: string;
  user: IUserBase;
  titularUser: IUserBase;
  bookedDate: string;
  experience: number;
  priorizedAt: string | undefined;
}

export interface IActivityScheduleCapacityView {
  available: number | null;
  booked: number;
  mode: 'DEFAULT' | 'LODGER_ONLY';
  vacancies: number | null;
}

export interface IActivityScheduleItemBase extends IEntityBase {
  activityScheduleId: string;
  description?: string;
  id: string;
  isActive: boolean;
  isRequired: boolean;
  name: string;
  price?: number | null;
  totalAmount: number;
  userId?: string | null;
  costCenter?: string;
  productCode?: string;
  productType?: string;
  productNature?: string;
  invoiceType?: 'NF' | 'ND';
}

export interface IWeekdayActivityScheduleBase {
  activityScheduleId: string;
  booked: number;
  capacityView: IActivityScheduleCapacityView;
  guestBooked: number;
  lodgerBooked: number;
  queueBooked: number;
  bookingPrice: number;
  guestAdditionalBookingPrice: number;
  bookings: IEventsScheduleBooking[];
  currentUserBooked: boolean;
  date: string;
  endTime: string;
  experience: number;
  isActive: boolean;
  isExpired: boolean;
  modalityId: string;
  modalityTitle: string;
  scheduleBookingPrice?: number;
  startDate: string;
  startTime: string;
  guestVacancies?: number;
  lodgerVacancies?: number;
  isLodgerVacanciesExclusive: boolean;
  queueVacancies: number;
  isQueueVacanciesExclusive: boolean;
  vacancies?: number;
  waveSide?: 'COMBO' | 'LEFT' | 'RIGHT';
}

export interface IActivityScheduleBase extends IEntityBase {
  activityId: string;
  allowLinkBookings: boolean;
  allowPartialTimeBookings: boolean;
  bookingPrice: number;
  guestAdditionalBookingPrice: number;
  bookingUrl: string;
  capacityView: IActivityScheduleCapacityView;
  daysOfWeek: Array<number>;
  endDate?: string;
  endTime: string;
  guestVacancies?: number;
  lodgerVacancies?: number;
  queueVacancies: number;
  isActive: boolean;
  isExpired: boolean;
  minUserExperience: number;
  modalityId?: string;
  waveSide?: 'COMBO' | 'LEFT' | 'RIGHT';
  startDate: string;
  startTime: string;
  totalVacancies?: number;
}

export interface IActivityItemBase extends IEntityBase {
  description?: string;
  isActive: boolean;
  name: string;
  // totalAmount: number;
}

export interface IActivityInvoiceConfigBase extends IEntityBase {
  bookingCostCenter?: string;
  bookingProductCode?: string;
  bookingProductType?: string;
  bookingProductNature?: string;
  bookingInvoiceType?: 'NF' | 'ND';
  guestBookingCostCenter?: string;
  guestBookingProductCode?: string;
  guestBookingProductType?: string;
  guestBookingProductNature?: string;
  guestBookingInvoiceType?: 'NF' | 'ND';
  activityId: string;
}

export interface IActivityBase extends IEntityBase {
  avatar?: string;
  avatarUrl?: string;
  bookingPrice?: number;
  description?: string;
  isActive: boolean;
  name: string;
  spotId: string;
}
